/** @jsx jsx */
/** @jsxFrag React.Fragment */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';

import { CollectionsModule } from '@coursera-web/cds-labs';
import type { Filter } from '@coursera-web/cds-labs';

import { useLocation } from 'js/lib/useRouter';

import type { Theme } from '@coursera/cds-core';

import type { withAccomplishmentsAndPaymentsRecommendationDataProps } from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationData';
import accomplishmentsAndPaymentsRecommendationSwitcher from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationSwitcher';
import DegreesCollection from 'bundles/accomplishments/components/DegreesCollection';
import { ProductVariantNames } from 'bundles/browse-common/constants';
import withPremiumProductCollections from 'bundles/browse-common/legacy/components/hoc/withPremiumProductCollections';
import type { PremiumProductProps } from 'bundles/browse-common/legacy/components/hoc/withPremiumProductCollections';
import type {
  DiscoveryCollection,
  DiscoveryCollectionEntity,
} from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionsForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import { CollectionsContainer } from 'bundles/collections-common/components/CollectionsContainer';
import { DISCO_ENTITY_TO_PRODUCT_TYPES } from 'bundles/collections-common/utils/collectionsUtils';
import { filterUndefined } from 'bundles/common/utils/commonUtils';
import { CourseraPlusAdContainer } from 'bundles/coursera-plus-common/components/CourseraPlusAdContainer';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';

import _t from 'i18n!nls/accomplishments';

type BaseProps = {
  recommendationCollections?: Array<DiscoveryCollection | CarouselCollection | null>;
  loadingRecommendations?: boolean;
};

type PremiumProductCollectionProps = {
  mastertrackListFromPremiumProductsCollection?: PremiumProductProps[];
  universityCertificateListFromPremiumProductsCollection?: PremiumProductProps[];
  premiumProductsCollectionLoading?: boolean;
};

type Props = BaseProps & PremiumProductCollectionProps;

const styles: Record<string, Interpolation<Theme>> = {
  recommendationsWrapper: () => css`
    .collection-name {
      font-weight: 600;
      margin-left: 10px !important;
      font-family: 'Source Sans Pro', Arial, sans-serif;
    }

    .recommendation-modules-container .collection-name {
      margin-left: 10px !important;
    }

    .rc-desktopCollection {
      .rc-BrowseCarousel {
        max-width: 100%;

        .slick-slider {
          width: 100%;
        }

        .slick-list {
          margin: 0;
        }
      }
    }

    .rc-Collection {
      .rc-desktopCollection {
        @media screen and (max-width: 768px) {
          max-height: 262px;
        }
      }
    }

    .adContainer {
      @media screen and (min-width: 769px) {
        margin-left: 10px;
      }
    }
  `,
};

const mapDiscoveryToCollectionEntity = (entity: DiscoveryCollectionEntity): CollectionEntity => {
  return {
    ...entity,
    link: entity?.url,
    imageUrl: entity?.imageUrl || '',
    partners:
      entity?.partners?.filter(filterUndefined)?.map((partner) => ({ ...partner, logo: partner.logo || '' })) || [],
    partnerLogos: entity?.partners?.filter(filterUndefined)?.map((partner) => partner.logo || '') || [],
    isPartOfCourseraPlus: ('isPartOfCourseraPlus' in entity && entity?.isPartOfCourseraPlus) || false,
    statusTags: entity && 'productCard' in entity ? entity?.productCard?.badges : [],
    productType: DISCO_ENTITY_TO_PRODUCT_TYPES[entity.__typename],
  };
};

const mapDiscoveryToCarouselCollection = (collection: DiscoveryCollection): CarouselCollection => ({
  ...collection,
  collectionId: collection.id,
  title: collection?.label || '',
  entities: collection?.entities?.filter(filterUndefined)?.map(mapDiscoveryToCollectionEntity) || [],
});

const getDescriptionForCertRecommendations = (certificateId?: string) => {
  switch (certificateId) {
    case 'university-certificate':
      return _t('Earn an affordable, university-issued credential from top schools');
    case 'mastertrack':
    default:
      return _t('Earn an university-issued credential and credit towards a degree');
  }
};

const AccomplishmentRecommendations = ({
  recommendationCollections,
  loadingRecommendations,
  mastertrackListFromPremiumProductsCollection,
  universityCertificateListFromPremiumProductsCollection,
  premiumProductsCollectionLoading,
}: Props) => {
  const location = useLocation();

  const [currentCertificateCollectionSetIndex, setCurrentCertificateCollectionSetIndex] = useState(0);

  const shouldShowCertRecommendations = useMemo(() => {
    const locationPage = location.pathname.split('/').filter((part) => part.length)[0];
    return locationPage === 'accomplishments';
  }, [location.pathname]);

  const { ownsCourseraPlus } = useCourseraPlusProductOwnerships();

  recommendationCollections?.forEach((collection) => {
    if (
      collection &&
      'id' in collection &&
      collection.id === 'RECOMMENDATIONS_MODULES:derived-day0-free-real-time-rec-algo$no_attribute$no_attribute'
    ) {
      collection.label = _t('Explore These Free Courses');
    }
  });

  const collections =
    recommendationCollections
      ?.filter(filterUndefined)
      .map((collection) =>
        'collectionId' in collection ? collection : mapDiscoveryToCarouselCollection(collection)
      ) ?? [];

  const certificateData = useMemo(() => {
    const mastertracksForCollectionCarousel = mastertrackListFromPremiumProductsCollection
      ?.map((product) => ({
        degree: { ...product, id: product.id ?? product.slug },
        __typename: 'DegreeListsV3',
      }))
      .slice(0, 6);

    const uCertsForCollectionCarousel = universityCertificateListFromPremiumProductsCollection
      ?.map((product) => ({
        degree: { ...product, id: product.id ?? product.slug },
        __typename: 'DegreeListsV3',
      }))
      .slice(0, 6);

    // Create individual collections only if data exists
    const mastertracksCollection = mastertracksForCollectionCarousel && {
      entries: mastertracksForCollectionCarousel,
      __typename: 'DegreeListV3Mastertrack',
      label: _t('MasterTrack® Certificates'),
    };

    const uCertsCollection = uCertsForCollectionCarousel && {
      entries: uCertsForCollectionCarousel,
      __typename: 'DegreeListV3UniversityCertificate',
      label: _t('University Certificates'),
    };

    const mappedMastertrackCollection = mastertracksCollection
      ? mapCollectionsForCarousel([mastertracksCollection])[0]
      : null;

    const mappedUCertsCollection = uCertsCollection ? mapCollectionsForCarousel([uCertsCollection])[0] : null;

    return [
      mappedMastertrackCollection?.entities && {
        id: 'mastertrack',
        filterName: _t('MasterTrack® Certificates'),
        collection: mappedMastertrackCollection,
      },
      mappedUCertsCollection?.entities && {
        id: 'university-certificate',
        filterName: _t('University Certificates'),
        collection: mappedUCertsCollection,
      },
    ].filter(Boolean);
  }, [mastertrackListFromPremiumProductsCollection, universityCertificateListFromPremiumProductsCollection]);

  const certificateFilters: Filter[] = useMemo(() => {
    return certificateData.map((item) => ({
      id: item.id,
      filterName: item.filterName,
    }));
  }, [certificateData]);

  return (
    <section
      className="rc-AccomplishmentRecommendations"
      data-testid="accomplishmentRecommendations"
      css={styles.recommendationsWrapper}
    >
      <CollectionsContainer collections={collections} isCollectionsLoading={loadingRecommendations} />
      <DegreesCollection key="degree-collection" />
      {shouldShowCertRecommendations && (premiumProductsCollectionLoading || certificateData?.length > 0) && (
        <CollectionsModule
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          collections={
            premiumProductsCollectionLoading
              ? undefined
              : [certificateData[currentCertificateCollectionSetIndex]?.collection]
          }
          isCollectionsLoading={premiumProductsCollectionLoading}
          headline={_t('MasterTrack® and University Certificates')}
          headlineComponent="h2"
          layout="text-top/image-bottom"
          currentCollectionSetIndex={currentCertificateCollectionSetIndex}
          filters={certificateFilters}
          onFilterChange={(index) => setCurrentCertificateCollectionSetIndex(index)}
          description={getDescriptionForCertRecommendations(certificateData[currentCertificateCollectionSetIndex]?.id)}
        />
      )}
      {!ownsCourseraPlus && <CourseraPlusAdContainer />}
    </section>
  );
};

const EnhancedAccomplishmentRecommendations =
  withPremiumProductCollections<withAccomplishmentsAndPaymentsRecommendationDataProps>(() => ({
    productVariants: [ProductVariantNames.Mastertrack, ProductVariantNames.UniversityCertificate],
  }))(AccomplishmentRecommendations);

export default accomplishmentsAndPaymentsRecommendationSwitcher(EnhancedAccomplishmentRecommendations);
